import { useMemo, useState, useCallback } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import type { CreateApiTokenDTO, GeneratedApiToken } from '../types/apiToken.types';
import { useToast } from '@/shared/hooks/use-toast';
import { Plus } from 'lucide-react';
import { logger } from '@/infrastructure/lib/logger';
import { useTranslation } from 'react-i18next';
import { useApiTokensQuery, useCreateToken, useRevokeToken, useRenewToken, useRegenerateToken } from '../hooks/useApiTokens';
import { TokenCard } from './TokenCard';
import { CreateTokenDialog } from './CreateTokenDialog';
import { AvailableApisList } from './AvailableApisList';

interface ProductApiTokensTabProps {
  workspaceId: string;
}

export function WorkspaceApiTokensTab({ workspaceId }: ProductApiTokensTabProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<GeneratedApiToken | null>(null);
  const [isApisOpen, setIsApisOpen] = useState(false);
  const [tokenCache, setTokenCache] = useState<Record<string, string>>({});

  const tokensQuery = useApiTokensQuery(workspaceId);
  const createMutation = useCreateToken(workspaceId);
  const revokeMutation = useRevokeToken(workspaceId);
  const renewMutation = useRenewToken(workspaceId);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tokens = tokensQuery.data || [];
  const hasAnyTokens = tokens.length > 0;

  const sortedTokens = useMemo(() => {
    return [...tokens].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens]);

  const handleRegenerateSuccess = useCallback((newToken: GeneratedApiToken) => {
    setGeneratedToken(newToken);
    setTokenCache(prev => ({ ...prev, [newToken.id]: newToken.token }));
    setCreateOpen(true);
  }, []);

  const regenerateMutation = useRegenerateToken(workspaceId, sortedTokens, handleRegenerateSuccess);

  const handleOpenCreate = () => {
    setGeneratedToken(null);
    setCreateOpen(true);
  };

  const handleCreate = async (payload: CreateApiTokenDTO) => {
    const token = await createMutation.mutateAsync(payload);
    setGeneratedToken(token);
    setTokenCache(prev => ({ ...prev, [token.id]: token.token }));
  };

  const handleCopy = async (tokenValue: string) => {
    const tokenString = typeof tokenValue === 'string' ? tokenValue : String(tokenValue);
    try {
      await navigator.clipboard.writeText(tokenString);
      toast({ title: t('workspaces.apiTokens.tokenCopied', 'Token copied to clipboard') });
    } catch (error: unknown) {
      const err = error as Error;
      logger.error('Failed to copy token to clipboard', { error: err?.message });
      toast({ variant: 'destructive', title: t('common.error'), description: t('workspaces.apiTokens.copyFailed', 'Failed to copy token') });
    }
  };

  const handleCopyFromList = async (tokenId: string) => {
    const cachedToken = tokenCache[tokenId];
    if (cachedToken) {
      await handleCopy(cachedToken);
    } else {
      const token = sortedTokens.find(t => t.id === tokenId);
      if (token) {
        try {
          await navigator.clipboard.writeText(token.token_prefix);
          toast({ title: t('workspaces.apiTokens.prefixCopied', 'Token prefix copied to clipboard') });
        } catch (error: unknown) {
          const err = error as Error;
          logger.error('Failed to copy token prefix', { error: err?.message });
          toast({ variant: 'destructive', title: t('common.error'), description: t('workspaces.apiTokens.copyPrefixFailed', 'Failed to copy token prefix') });
        }
      }
    }
  };

  const handleToggleActive = (tokenId: string, active: boolean) => {
    if (active) {
      renewMutation.mutate(tokenId);
    } else {
      revokeMutation.mutate(tokenId);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t('workspaces.apiTokens.title')}</CardTitle>
            <CardDescription>{t('workspaces.apiTokens.description')}</CardDescription>
          </div>
          <Button size="sm" onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 mr-2" />
            {t('workspaces.apiTokens.create')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <AvailableApisList isOpen={isApisOpen} onOpenChange={setIsApisOpen} />

        {!tokensQuery.isLoading && !hasAnyTokens ? (
          <div className="text-center py-8 border rounded-lg">
            <p className="text-lg font-semibold mb-2">{t('workspaces.apiTokens.noTokens')}</p>
            <p className="text-sm text-muted-foreground mb-4">{t('workspaces.apiTokens.noTokensDesc')}</p>
            <Button onClick={handleOpenCreate}>{t('workspaces.apiTokens.createFirst')}</Button>
          </div>
        ) : null}

        <div className="space-y-4">
          {sortedTokens.map((token) => (
            <TokenCard
              key={token.id}
              token={token}
              hasCachedToken={!!tokenCache[token.id]}
              onCopy={handleCopyFromList}
              onRegenerate={(id) => regenerateMutation.mutate(id)}
              onToggleActive={handleToggleActive}
              isRegenerating={regenerateMutation.isPending}
              isTogglingActive={revokeMutation.isPending || renewMutation.isPending}
            />
          ))}
        </div>

        <CreateTokenDialog
          workspaceId={workspaceId}
          open={createOpen}
          onOpenChange={setCreateOpen}
          generatedToken={generatedToken}
          onCreate={handleCreate}
          onCopy={handleCopy}
          isCreating={createMutation.isPending}
        />
      </CardContent>
    </Card>
  );
}
